import React, { useRef, useEffect } from "react";
import { useIntl } from "react-intl";

import { Dropdown, DropdownMenu } from "src/app/components/Dropdown/Dropdown";
import { ReactComponent as AIChatIcon } from "src/images/ai-chat.svg";
import { ReactComponent as ChevronDownIcon } from "src/images/chevron-down.svg";
import { fetchConversation } from "../../utils/chatApi";
import { timestampToReadableDate } from "../../utils/formatTimestamp";
import type { Message } from "../../utils/types";
import { translateMessage } from "src/app/methods/translateMessage";
import Skeleton from "src/app/components/Skeleton/Skeleton";
import { useConversationHistory } from "./useConversationHistory";
import { showErrorToast } from "src/utils/methods";
import { ReactComponent as TickIcon } from "src/images/tick-blue.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useJwtRefresh } from "../../hooks/useJwtRefresh";

interface ConversationHistoryDropdownProps {
  onConversationLoad: (
    conversationId: number,
    messages: Message[],
    isArchived?: boolean,
  ) => void;
  onLoadingConversation: (isLoading: boolean) => void;
  selectedConversationId: number | null;
  setSelectedConversationId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  getCurrentConversationId: () => number | null;
}

export default function ConversationHistoryDropdown({
  onConversationLoad,
  onLoadingConversation,
  selectedConversationId,
  setSelectedConversationId,
  getCurrentConversationId,
}: ConversationHistoryDropdownProps) {
  const conversationFetchController = useRef<AbortController | null>(null);

  const { conversations, isLoading, refetchConversations } =
    useConversationHistory();
  const { getValidToken } = useJwtRefresh();

  const intl = useIntl();
  const prevConversationIdRef = useRef<number | null>(null);

  // Watch for when a conversation is first created and update the selected ID
  useEffect(() => {
    const currentId = getCurrentConversationId();

    // Check if a conversation was just created (went from null to a value)
    if (
      currentId !== null &&
      prevConversationIdRef.current === null &&
      selectedConversationId === null
    ) {
      // Set the selected conversation ID to the newly created one
      setSelectedConversationId(currentId);
      // Refetch conversations to include the newly created one
      refetchConversations();
    }

    prevConversationIdRef.current = currentId;
  }, [
    getCurrentConversationId,
    selectedConversationId,
    setSelectedConversationId,
    refetchConversations,
  ]);

  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId,
  );

  const selectedConversationLabel =
    selectedConversationId === null || !selectedConversation
      ? translateMessage({
          intl,
          id: "ws_new_chat",
          defaultMessage: "New chat",
        })
      : selectedConversation.custom_attributes?.conversation_title ??
        timestampToReadableDate(selectedConversation.created_at);

  const handleSelectConversation = async (conversationId: number) => {
    const token = await getValidToken();
    try {
      if (!token) throw new Error("No valid token");
      conversationFetchController.current?.abort();
      conversationFetchController.current = new AbortController();

      setSelectedConversationId(conversationId);

      onLoadingConversation(true);
      const messages = await fetchConversation(
        conversationId,
        token,
        conversationFetchController.current.signal,
      );
      const conversation = conversations.find((c) => c.id === conversationId);
      const isArchived = conversation?.custom_attributes?.archived ?? false;
      onConversationLoad(conversationId, messages, isArchived);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      console.error("Failed to fetch conversation:", err);
      showErrorToast();
    } finally {
      onLoadingConversation(false);
    }
  };

  return isLoading ? (
    <Skeleton width={114} height={22} />
  ) : (
    <Dropdown>
      {(isOpen, setIsOpen) => (
        <>
          <button
            className={`ai-chat__history-trigger${isOpen ? " ai-chat__history-trigger--open" : ""}`}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <span className="ai-chat__history-trigger-label">
              {selectedConversationLabel}
            </span>
            <ChevronDownIcon className="ai-chat__history-trigger-chevron icon-dark" />
          </button>
          <DropdownMenu isOpen={isOpen}>
            {conversations.length > 0 ? (
              conversations.map((conversation) => {
                const formattedDate = timestampToReadableDate(
                  conversation.created_at,
                );
                const isSelected = conversation.id === selectedConversationId;
                return (
                  <button
                    key={conversation.id}
                    className="ai-chat__history-item"
                    onClick={() => {
                      setIsOpen(false);
                      if (isSelected) return;
                      handleSelectConversation(conversation.id);
                    }}
                  >
                    <AIChatIcon className="ai-chat__history-item-icon" />
                    <span className="ai-chat__history-item-title">
                      {conversation.custom_attributes?.conversation_title ??
                        formattedDate}
                    </span>
                    {isSelected && (
                      <TickIcon className="ai-chat__history-item-tick" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="ai-chat__history-no-chat-history">
                <IDHFormattedMessage
                  id="ws_no_chat_history"
                  defaultMessage="No chat history"
                />
              </div>
            )}
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
}
